import { Container, Skeleton } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useStudentAssessment, useStudentSimulation, useUser } from '../../api';
import AppBar from '../../app-bar/AppBar';
import Assessments from '../../assessments/Assessments';
import { useCheckRole } from '../../auth';
import BackLink from '../../back-link/BackLink';
import Breadcrumbs from '../../breadcrumbs/Breadcrumbs';
import KSimHeader from '../../k-sim-header/KSimHeader';
import { apiPaths, queryClient } from '../../queryClient';
import { useSignalREffect } from '../../signalr/SignalRContext';
import SimulationCard from '../../simulation-card/SimulationCard';
import SimulationSkeleton from '../../skeletons/SimulationSkeleton';
import {
  AssessmentUpdatedNotification,
  ExerciseType,
  Assessment as IAssessment,
  ParticipantNotification,
  SessionSuccessStatuses,
  SimulationEventMessage,
} from '../../types';
import { parseTime } from '../../utils/parseTime';
import { useActiveCompany } from '../../utils/useCompany';

/**
 * Simulation detail page of a student from the instructor's point of view
 */
const StudentSimulation = () => {
  useCheckRole(['Instructor']);

  const { exerciseId, simulationId, userId } = useParams();
  const activeCompany = useActiveCompany();
  const { simulation, simulationError, simulationLoading } = useStudentSimulation(exerciseId, userId, simulationId);
  const technologyPlatform = simulation?.simulator?.platform;
  const location = useLocation();

  const { assessments, assessmentsError, assessmentsLoading } = useStudentAssessment(
    exerciseId,
    userId,
    simulationId,
    technologyPlatform,
  );
  const { user, userError, userLoading } = useUser(exerciseId, userId);
  const [assessmentsState, setAssessments] = useState<IAssessment[]>([]);

  useEffect(() => {
    assessments && setAssessments(assessments);
  }, [assessments]);

  useSignalREffect(
    SimulationEventMessage.AssessmentUpdated,
    (...args) => {
      const { assessments, simulationId: sessionId, userId: id } = args[0] as AssessmentUpdatedNotification;
      if (sessionId === simulationId && userId === id) {
        setAssessments(assessments);
      }
    },
    [],
  );

  useSignalREffect(
    SimulationEventMessage.ParticipantSimulationCompleted,
    (...args) => {
      const { status, simulationId: id } = args[0] as ParticipantNotification;
      if (id === simulationId && status === SessionSuccessStatuses.Completed) {
        queryClient
          .fetchQuery([
            {
              path: apiPaths.teach.simulation(activeCompany?.companyId, exerciseId, userId, simulationId),
            },
          ])
          .then(() => {
            queryClient.fetchQuery([
              {
                path: apiPaths.teach.assessment(activeCompany?.companyId, userId, simulationId, technologyPlatform),
              },
            ]);
          });
      }
    },
    [activeCompany, simulation],
  );

  if (simulationLoading || userLoading) {
    return (
      <SimulationSkeleton
        breadcrumbs={[
          { label: 'Home', to: '/teach' },
          { to: '../..', label: <Skeleton width={160} /> },
          { to: '..', label: <Skeleton width={160} /> },
          { label: <Skeleton width={120} /> },
        ]}
      />
    );
  }

  if (simulationError || !simulation || userError) {
    throw simulationError || userError;
  }

  const { endTime, exercise, name, position, startTime, status, simulator, student } = parseTime(simulation);

  return (
    <>
      <AppBar title="Exercise" />
      <Breadcrumbs
        items={[
          { to: '/', label: 'Home' },
          { to: '../..', label: exercise.title },
          location?.state === ExerciseType.Classroom
            ? { to: `../../simulations/${simulation.id}`, label: 'Team' }
            : { to: '../', label: user?.name },
          { to: './', label: name },
        ]}
        sx={{ display: { xs: 'none', sm: 'flex' } }}
      />
      <Container>
        <BackLink label="Back to student" sx={{ display: { xs: 'inline-block', sm: 'none' }, my: 2 }} to=".." />
        <SimulationCard
          adHoc={exercise.adHoc}
          endTime={endTime}
          exerciseName={exercise?.name}
          exerciseType={exercise?.type}
          position={position}
          status={status}
          startTime={startTime}
          simulatorType={simulator.type}
          studentAvatar={student?.avatar}
          studentName={student?.name}
        />
        <KSimHeader
          exerciseType={exercise.type}
          simulatorType={simulator.type}
          sx={{ display: { xs: 'grid', sm: 'none' }, mt: 1, mb: { xs: 2, sm: 4 } }}
        />
        <Assessments
          assessments={assessmentsState}
          loading={assessmentsLoading}
          error={assessmentsError}
          simulationStatus={simulation.status}
        />
      </Container>
    </>
  );
};

export default StudentSimulation;
